import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Download, Eye, DollarSign, Filter, AlertCircle } from 'lucide-react';
import * as documentService from '../../services/documentService';
import { Document } from '../../services/documentService';
import { getUserData } from '../../utils/authUtils';

/**
 * Vista de facturas generadas por IMAGECLEC
 *
 * Este componente muestra las facturas que IMAGECLEC ha subido para el cliente.
 * Las facturas se almacenan en S3 con tipo "factura".
 */

interface Invoice extends Document {
  // Campos adicionales si se necesitan
}

const InvoicesView: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<string>('todos');
  const [invoices, setInvoices] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = getUserData()?.userId ?? 0;

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const currentYear = new Date().getFullYear();
  const años = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => currentYear - i);

  // Cargar facturas al montar el componente
  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      // Cargar documentos de tipo "factura" e "informe_estadistico"
      const [facturasResponse, reportesResponse] = await Promise.all([
        documentService.getDocumentsByType(userId, 'factura'),
        documentService.getDocumentsByType(userId, 'informe_estadistico')
      ]);

      // Combinar ambos tipos y ordenar por fecha de subida (más recientes primero)
      const allDocuments = [...facturasResponse.documents, ...reportesResponse.documents]
        .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

      setInvoices(allDocuments);
    } catch (err) {
      console.error('Error al cargar documentos:', err);
      setError('Error al cargar documentos. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const yearMatch = invoice.year === selectedYear;
      if (selectedMonth === 'todos') return yearMatch;
      const monthIndex = meses.indexOf(selectedMonth) + 1;
      return yearMatch && invoice.month === monthIndex;
    });
  }, [invoices, selectedYear, selectedMonth, meses]);

  const handleDownload = async (invoice: Document) => {
    try {
      await documentService.downloadDocument(invoice.id, invoice.originalFileName);
    } catch (err) {
      console.error('Error al descargar:', err);
      setError('Error al descargar la factura');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleView = async (invoice: Document) => {
    try {
      await documentService.viewDocument(invoice.id);
    } catch (err) {
      console.error('Error al visualizar:', err);
      setError('Error al visualizar la factura');
      setTimeout(() => setError(null), 5000);
    }
  };

  const getTipoBadge = (documentType: string) => {
    switch (documentType) {
      case 'factura':
        return (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Factura
          </span>
        );
      case 'informe_estadistico':
        return (
          <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
            Informe Estadístico
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
            Otro
          </span>
        );
    }
  };

  const { totalDocumentos, totalFacturas, totalReportes } = useMemo(() => ({
    totalDocumentos: filteredInvoices.length,
    totalFacturas: filteredInvoices.filter(inv => inv.documentType === 'factura').length,
    totalReportes: filteredInvoices.filter(inv => inv.documentType === 'informe_estadistico').length,
  }), [filteredInvoices]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FileText className="text-blue-600" size={28} />
        Documentos de IMAGECLEC
      </h2>

      <p className="text-sm text-gray-600 mb-6">
        Facturas e informes estadísticos generados por IMAGECLEC para tu centro médico.
      </p>

      {/* Mensaje de error */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Documentos</p>
              <p className="text-2xl font-bold text-blue-900">{totalDocumentos}</p>
            </div>
            <FileText className="text-blue-600" size={32} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Facturas</p>
              <p className="text-2xl font-bold text-green-900">{totalFacturas}</p>
            </div>
            <DollarSign className="text-green-600" size={32} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Informes Estadísticos</p>
              <p className="text-2xl font-bold text-purple-900">{totalReportes}</p>
            </div>
            <FileText className="text-purple-600" size={32} />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={20} className="text-gray-600" />
          <h3 className="font-semibold text-gray-800">Filtros</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {años.map(año => (
                <option key={año} value={año}>{año}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="todos">Todos los meses</option>
              {meses.map(mes => (
                <option key={mes} value={mes}>{mes}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Documentos */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Documentos {selectedMonth !== 'todos' ? `de ${selectedMonth}` : ''} {selectedYear}
        </h3>

        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['Tipo', 'Periodo', 'Versión', 'Archivo', 'Fecha Subida', 'Tamaño', 'Acciones'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 animate-pulse">
                {[...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded-full w-20" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-40" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-28" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded w-14" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-3 opacity-50" />
            <p className="text-lg">No hay documentos para el período seleccionado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Periodo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Versión</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Archivo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fecha Subida</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tamaño</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {getTipoBadge(invoice.documentType)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {documentService.formatPeriod(invoice.year, invoice.month)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      v{invoice.version}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText size={18} className="text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">{invoice.originalFileName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {new Date(invoice.uploadedAt).toLocaleDateString('es-CL', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {documentService.formatFileSize(invoice.fileSize)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(invoice)}
                          className="text-blue-600 hover:text-blue-800 transition p-1 rounded hover:bg-blue-50"
                          title="Ver documento"
                        >
                          <Eye size={20} />
                        </button>
                        <button
                          onClick={() => handleDownload(invoice)}
                          className="text-green-600 hover:text-green-800 transition p-1 rounded hover:bg-green-50"
                          title="Descargar documento"
                        >
                          <Download size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesView;
